import BillFormatModal from './form/BillFormatModal';
import { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Input, Row, Select, Table } from 'antd';
import {
  DeleteFilled,
  EditFilled,
  PlusCircleOutlined,
} from '@ant-design/icons';
import { SearchOutlined } from '@mui/icons-material';
import { billFormatListTableHeader as columns } from './common/billformatHeader';
import { useGetApiList } from '@/apiError/apiGetList';
import { globalSearch } from '@/utils/search/Search';
import { BillFormatDataType, FilterType } from './common/types';
import { useDeleteApi } from '@/apiError/apiDelete';
import GlobalModal from '@/utils/modal/Modal';
import '@/assets/style/modern-group-list.css';
// import { Link } from 'react-router-dom';

const BillFormatAdmin = () => {
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [dataSource, setDataSource] = useState<BillFormatDataType[]>([]);
  const [scrollY, setScrollY] = useState<number>(400);
  const [filter, setFilter] = useState<FilterType>({
    companyId: '',
    branchId: '',
    transactionType: '',
  });
  const [referenceId, setReferenceId] = useState<string | null>(null);
  const [form] = Form.useForm();

  const {
    data: { data: billFormatList } = {},
    isPending: loading,
    isSuccess,
    refetch,
  } = useGetApiList('admin/bill-format/');

  const { mutateAsync: deleteData, isPending: deleteLoading } =
    useDeleteApi() as ReturnType<typeof useDeleteApi> & {
      mutateAsync: (val: string) => Promise<void>;
    };

  const handleDelete = async () => {
    await deleteData(`admin/bill-format/${referenceId}/`);
    handleCancel();
  };

  const handleCancel = () => {
    setOpenDeleteModal(false);
    setOpenModal(false);
    form.resetFields();
    setReferenceId(null);
    refetch();
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleOpenEditModal = (referenceId: string) => {
    setReferenceId(referenceId);
    setOpenModal(true);
  };

  const handleOpenDeleteModal = (referenceId: string) => {
    setReferenceId(referenceId);
    setOpenDeleteModal(true);
  };

  useEffect(() => {
    // Function to calculate dynamic table height
    const updateTableHeight = () => {
      const windowHeight = window.innerHeight;
      const tableOffset = 320; // Adjust based on header, padding, etc.

      const updatedHeight = Math.max(windowHeight - tableOffset, 100);
      setScrollY(updatedHeight);
    };
    updateTableHeight(); // Initial height set
    window.addEventListener('resize', updateTableHeight); // Update on resize
    return () => window.removeEventListener('resize', updateTableHeight);
  }, []);

  useEffect(() => {
    let filteredData = billFormatList;

    if (filter.companyId) {
      filteredData = filteredData.filter(
        (item: any) => item.companyId === filter.companyId,
      );
    }

    if (filter.branchId) {
      filteredData = filteredData.filter(
        (item: any) => item.branchId === filter.branchId,
      );
    }

    if (filter.transactionType) {
      filteredData = filteredData.filter(
        (item: any) => item.transactionType === filter.transactionType,
      );
    }

    setDataSource(filteredData);
  }, [filter, billFormatList]);
  return (
    <>
      {openModal && (
        <BillFormatModal
          open={openModal}
          handleCancel={handleCancel}
          form={form}
          referenceId={referenceId}
          setReferenceId={setReferenceId}
        />
      )}
      <GlobalModal
        isVisible={openDeleteModal}
        onConfirm={handleDelete}
        onClose={handleCancel}
        centered={true}
        type="delete"
        customContent="Are you sure you want to delete this bill format?"
      />

      <div className="modern-group-page">
        <div className="modern-group-header-row">
          <div className="modern-group-header">
            <h1 className="modern-group-title">Bill Format</h1>
            <p className="modern-group-subtitle">
              Manage and organize your bill formats
            </p>
          </div>
          <div className="modern-group-actions">
            <Input
              className="header-search"
              name="query"
              style={{ width: '260px' }}
              placeholder="Search Bill Format..."
              value={searchQuery}
              prefix={<SearchOutlined />}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            <Button
              className="centered"
              type="primary"
              onClick={handleOpenModal}
            >
              <PlusCircleOutlined />
              Create
            </Button>
          </div>
        </div>

        {/* Filter bar */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
            marginTop: 14,
          }}
        >
          <Select
            className="header-select"
            placeholder="Filter Company"
            showSearch
            allowClear
            value={filter.companyId || undefined}
            optionFilterProp="children"
            style={{ width: '240px' }}
            filterOption={(input: any, option: any): boolean => {
              return option.label.toLowerCase().includes(input.toLowerCase());
            }}
            options={billFormatList
              ?.map((item: any) => ({
                label: item.companyName,
                value: item.companyId,
              }))
              .filter(
                (item: any, index: any, self: any) =>
                  index === self.findIndex((t: any) => t.value === item.value),
              )}
            onChange={(value) => {
              setFilter({
                ...filter,
                companyId: value,
              });
            }}
          />
          <Select
            className="header-select"
            placeholder="Filter Branch"
            showSearch
            allowClear
            value={filter.branchId || undefined}
            style={{ width: '240px' }}
            optionFilterProp="children"
            filterOption={(input: any, option: any): boolean => {
              return option.label.toLowerCase().includes(input.toLowerCase());
            }}
            options={billFormatList
              ?.map((item: any) => ({
                label: item.branchName,
                value: item.branchId,
              }))
              .filter(
                (item: any, index: number, self: any) =>
                  index === self.findIndex((t: any) => t.value === item.value),
              )}
            onChange={(value) => {
              setFilter({
                ...filter,
                branchId: value,
              });
            }}
          />
          <Select
            className="header-select"
            placeholder="Filter Transaction Type"
            showSearch
            allowClear
            value={filter.transactionType || undefined}
            style={{ width: '240px' }}
            optionFilterProp="children"
            filterOption={(input: any, option: any): boolean => {
              return option.label.toLowerCase().includes(input.toLowerCase());
            }}
            options={billFormatList
              ?.map((item: any) => ({
                label: item.transactionType,
                value: item.transactionType,
              }))
              .filter(
                (item: any, index: any, self: any) =>
                  index === self.findIndex((t: any) => t.value === item.value),
              )}
            onChange={(value) => {
              setFilter({
                ...filter,
                transactionType: value,
              });
            }}
          />
          <Button
            onClick={() =>
              setFilter({ companyId: '', branchId: '', transactionType: '' })
            }
          >
            Clear
          </Button>
        </div>

        <div className="table-responsive modern-table-wrapper">
          <Table<BillFormatDataType>
            size="large"
            bordered
            loading={loading}
            tableLayout="auto"
            columns={columns?.map((col) => ({
              ...col,
              onCell: () => ({
                style: {
                  padding: '6px 12px',
                },
              }),
            }))}
            className="ant-border-space"
            style={{ textAlign: 'center' }}
            scroll={{ x: true, y: scrollY }}
            pagination={false}
            dataSource={
              isSuccess
                ? globalSearch(dataSource, searchQuery)?.map(
                    (billData, index) => {
                      return {
                        key: billData.referenceId,
                        index: index + 1,
                        companyId: billData.companyName,
                        branchId: billData.branchName,
                        transactionType: billData.transactionType,
                        storeName: billData?.stores
                          ?.map((store: any) => store?.name)
                          ?.filter(Boolean)
                          ?.join(', '),
                        billPrefix: billData.billPrefix,
                        billSuffix: billData.billSuffix,
                        startNumber: billData?.startNumber,
                        // financialYearId: billData?.financialYear,
                        action: (
                          <div className="ant-action tw-flex tw-gap-4 tw-justify-center tw-align-center">
                            <span
                              className="edit tw-inline-flex tw-cursor-pointer hover:tw-bg-blue-200 tw-rounded-full tw-p-1"
                              onClick={() =>
                                handleOpenEditModal(billData.referenceId)
                              }
                            >
                              <EditFilled className="tw-text-blue-600" />
                            </span>
                            <span
                              className="text-danger tw-inline-flex tw-cursor-pointer hover:tw-bg-red-200 tw-rounded-full tw-p-1"
                              onClick={() =>
                                handleOpenDeleteModal(billData.referenceId)
                              }
                            >
                              <DeleteFilled />
                            </span>
                          </div>
                        ),
                      };
                    },
                  )
                : []
            }
          />
        </div>
      </div>
    </>
  );
};

export default BillFormatAdmin;
